import {
  LARGE_MOBILE,
  LOGIN_PATH,
  OAUTH_AUTHORIZE_LOGIN_PATH,
  REGISTER_PATH,
  REGISTER_VERIFY_EMAIL_PATH,
  RESET_PASSWORD_PATH,
} from "common/constants";
import LoginPage from "features/auth/LoginPage";
import React, { useContext, useEffect } from "react";
import RegisterForm from "features/auth/RegisterForm";
import ResetPassword from "features/auth/ResetPassword";
import styled, { css, useTheme } from "styled-components";
import useQuery from "utils/hooks/useQuery";
import { localStorageService } from "utils/localStorageService";
import Layout from "antd/es/layout";
import { useDispatch } from "react-redux";
import cornixLogo from "images/svg_logo.svg";
import HideShowTransition from "common/Animations/HideShowTransition";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import VerifyEmail from "features/auth/VerifyEmail";
import useMedia from "utils/hooks/useMedia";
import SecuritySection from "features/auth/SecuritySection";
import { sharedLib } from "@shared/services/shared.service.ts";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import LanguageSelectorPopover from "common/Home/Header/LanguageSelectorPopover.tsx";
import NewLookAuthLayout from "features/auth/newLook/NewLookAuthLayout";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const Auth = () => {
  const { brandingData } = useBranding();
  const isWhitelabel = brandingData?.whitelabel;
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const appTheme = useTheme();
  const dispatch = useDispatch();
  const query = useQuery();
  const uuid = query.get("uuid");
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);

  useEffect(() => {
    dispatch(sharedLib.slices.common.actions.setIsAuth(true));
    return () => {
      dispatch(sharedLib.slices.common.actions.setIsAuth(false));
    };
  }, []);

  const PATH_TO_COMPONENT: Record<string, React.JSX.Element> = {
    [LOGIN_PATH]: <LoginPage />,
    [OAUTH_AUTHORIZE_LOGIN_PATH]: <LoginPage />,
    [RESET_PASSWORD_PATH]: <ResetPassword />,
    [REGISTER_VERIFY_EMAIL_PATH]: <VerifyEmail />,
    [REGISTER_PATH]: <RegisterForm />,
  };
  const paths = Object.keys(PATH_TO_COMPONENT);
  const components = Object.values(PATH_TO_COMPONENT);
  const [componentIndex] = useRoutesMatch(paths) || [0];

  const isLoginOrRegister = !!useRoutesMatch([
    LOGIN_PATH,
    REGISTER_PATH,
    OAUTH_AUTHORIZE_LOGIN_PATH,
  ]);

  useEffect(() => {
    if (uuid && isLoginOrRegister) {
      localStorageService.setUuid(uuid);
    }
  }, [uuid, isLoginOrRegister]);

  let logoSrc = appTheme.isDarkMode ? cornixWhiteLogo : cornixLogo;
  if (isWhitelabel) {
    logoSrc = appTheme.isDarkMode
      ? brandingData?.dark_mode_logo
      : brandingData?.light_mode_logo;
  }

  const logo = logoSrc && (
    <StyledLogoImage src={logoSrc} alt="logo" $isMobile={isMobile} />
  );
  const languageSelector = !isExternalConnectAccount && (
    <LanguageSelectorPopover />
  );

  const footer = (
    <FooterWrapper>
      <span
        style={{ color: appTheme.colors.white, padding: isMobile ? 24 : 0 }}
      >
        {`© 2018 - ${new Date().getFullYear()}${
          isWhitelabel ? "" : " Cornix.io"
        }. ${t("copyright.all_rights_reserved")}`}
      </span>
    </FooterWrapper>
  );

  if (appTheme.isNewLook && !isExternalConnectAccount) {
    return (
      <NewLookAuthLayout below={isLoginOrRegister && <SecuritySection />}>
        <HideShowTransition
          components={components}
          stepOrTab={componentIndex}
          style={{ position: "relative", width: "100%" }}
        />
      </NewLookAuthLayout>
    );
  }

  return (
    <Wrapper $isExternalConnectAccount={isExternalConnectAccount}>
      {!isMobile && !isExternalConnectAccount && (
        <HeaderWrapper>
          {logo}
          <LanguageButtonWrapper>{languageSelector}</LanguageButtonWrapper>
        </HeaderWrapper>
      )}
      <Content $isExternalConnectAccount={isExternalConnectAccount}>
        <ComponentWrapper $isExternalConnectAccount={isExternalConnectAccount}>
          <ComponentInnerWrapper>
            {isMobile && !isExternalConnectAccount && (
              <MobileHeaderRow>
                {logo}
                {languageSelector}
              </MobileHeaderRow>
            )}
            <HideShowTransition
              components={components}
              stepOrTab={componentIndex}
              style={{
                position: "relative",
                width: "100%",
                paddingBottom: isLoginOrRegister ? 32 : 70,
              }}
            />
          </ComponentInnerWrapper>
          {isLoginOrRegister && <SecuritySection />}
          {isMobile && !isExternalConnectAccount && (
            <MobileFooter>{footer}</MobileFooter>
          )}
        </ComponentWrapper>
      </Content>
      {!isMobile && !isExternalConnectAccount && (
        <LayoutFooter>{footer}</LayoutFooter>
      )}
    </Wrapper>
  );
};

export default Auth;

const StyledLogoImage = styled.img<{ $isMobile?: boolean }>`
  ${(p) =>
    p.$isMobile
      ? `
    margin: 12px 0;
    height: 40px;
    align-self: flex-start;
    `
      : `
    position: absolute;
    left: 20px;
    top: 16px;
    height: 60px;
  `};
`;
const Wrapper = styled(Layout)<{ $isExternalConnectAccount?: boolean }>`
  ${(p) =>
    !p.$isExternalConnectAccount &&
    `
    height: 100vh;
    width: 100vw;
    @supports (height: 100dvh) {
      height: 100dvh;
    }
  `};
  display: flex;
  background: ${(p) => p.theme.colors.bgColor};
`;
const Content = styled(Layout.Content)<{ $isExternalConnectAccount?: boolean }>`
  ${(p) =>
    !p.$isExternalConnectAccount &&
    `
    overflow: auto;
    padding: 40px 40px 0 40px;
  `};
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
  @media (max-width: ${LARGE_MOBILE}) {
    padding: 0;
    flex-direction: column;
  }
`;
const ComponentWrapper = styled.div<{ $isExternalConnectAccount?: boolean }>`
  position: relative;
  ${(p) =>
    !p.$isExternalConnectAccount &&
    `
    margin: auto 0;
  `};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: ${LARGE_MOBILE}) {
    min-height: 100%;
    justify-content: space-between;
  }
`;
const ComponentInnerWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  @media (max-width: ${LARGE_MOBILE}) {
    padding: 0 20px;
  }
`;
const HeaderWrapper = styled.div`
  width: 100%;
  z-index: 1;
`;
const LanguageButtonWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 20px;
`;
const MobileHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const FooterWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  padding: 0 50px;
  text-align: center;
`;
const footerBgStyle = css`
  background: ${(p) => p.theme.colors.layoutFooterColor};
  width: 100%;
`;
const LayoutFooter = styled(Layout.Footer)`
  ${footerBgStyle}
`;
const MobileFooter = styled.div`
  ${footerBgStyle}
`;
